import { Link, useParams } from 'react-router-dom';
import { Seo } from '../components/Seo.jsx';
import { useApi } from '../hooks/useApi.js';
import { fetchNews, fetchNewsBySlug } from '../lib/api.js';
import { PageHeader } from '../components/PageHeader.jsx';
import { Prose } from '../components/Prose.jsx';
import { NewsGrid } from '../components/NewsGrid.jsx';
import { usePage } from '../hooks/usePage.js';

const formatDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }); }
  catch { return ''; }
};

const CAT = { notice: 'Notice', story: 'Story', event: 'Event', achievement: 'Achievement', in_memoriam: 'In memoriam' };

export function NewsDetailPage() {
  const { slug } = useParams();
  const fallbackBanner = usePage('news').banner;
  const { data: article, loading, error } = useApi(() => fetchNewsBySlug(slug), [slug]);
  const { data: relatedResponse } = useApi(
    () => fetchNews({ limit: 4, ...(article?.category ? { category: article.category } : {}) }),
    [article?.category],
  );

  if (loading) {
    return (
      <section>
        <div className="container"><p>Loading article…</p></div>
      </section>
    );
  }

  if (error || !article) {
    return (
      <>
        <PageHeader
          crumbs={[{ label: 'Home', to: '/' }, { label: 'News', to: '/news' }, { label: 'Not found' }]}
          title="Article not found"
          lede="That article may have been removed, or the link is incorrect."
        />
        <section>
          <div className="container">
            <Link to="/news" className="btn btn-ghost">← Back to news</Link>
          </div>
        </section>
      </>
    );
  }

  const related = (relatedResponse?.items || []).filter((n) => n.slug !== slug).slice(0, 3);

  return (
    <>
      <Seo
        title={article.title}
        description={article.excerpt || article.title}
        image={article.heroImage}
        type="article"
      />

      <PageHeader
        crumbs={[
          { label: 'Home', to: '/' },
          { label: 'News', to: '/news' },
          { label: CAT[article.category] || article.category, to: `/news?category=${article.category}` },
          { label: article.title },
        ]}
        title={article.title}
        lede={article.excerpt}
        image={article.heroImage || fallbackBanner}
      />

      <section>
        <div className="container">
          <div style={{ color: 'var(--ink-3)', fontSize: 14, marginBottom: 32, textAlign: 'center' }}>
            {CAT[article.category] || article.category} · {formatDate(article.publishedAt || article.createdAt)}
          </div>
          {article.body
            ? <Prose html={article.body} />
            : <Prose><p className="lede">No further detail published yet.</p></Prose>}
        </div>
      </section>

      {related.length > 0 && (
        <section className="bg-cream">
          <div className="container">
            <div className="section-eyebrow">Related</div>
            <h2 className="section-title" style={{ margin: '12px 0 32px' }}>Read <em>more</em></h2>
            <NewsGrid items={related} columns={3} />
          </div>
        </section>
      )}
    </>
  );
}
